import {constant, defaultTo, divide, isEmpty, isNull, isUndefined, multiply, negate, noop, stubString} from 'lodash/fp'
import {ifElse} from 'ramda'
import {pipeline as flow} from 'ts-pipe-compose'

import {PathsIn} from './src'

export * from './src'
export {window} from './browser-monads'
export {addQuery} from './url'
export {getUUID} from './uuid'
export {default as copyToClipboard} from 'copy-to-clipboard'
/** @see https://github.com/leizongmin/js-xss */
export {default as sanitize} from 'xss'
// ре-экспортим лодаш, чтобы в будущем либо избавиться от него, либо заменить на ramda
// TODO: Избавиться от sortBy в этом тикете https://st.yandex-team.ru/CARGODEV-27628
export {chunk, cloneDeep, debounce} from 'lodash'
export {
  add,
  allPass,
  anyPass,
  assign,
  castArray,
  concat,
  cond,
  curryRight,
  defaults,
  each,
  eq,
  equals,
  every,
  filter,
  find,
  findIndex,
  findLast,
  flatten,
  groupBy,
  gt,
  gte,
  has,
  head,
  includes,
  isArray,
  isBoolean,
  isEqual,
  isNil,
  isNumber,
  isObject,
  isString,
  join,
  last,
  map,
  merge,
  omit,
  omitBy,
  pick,
  pickBy,
  prop,
  random,
  range,
  reduce,
  replace,
  reverse,
  round,
  set,
  some,
  sortBy,
  split,
  stubArray,
  stubFalse,
  stubObject,
  stubTrue,
  take,
  takeRight,
  template,
  throttle,
  toLower,
  trim,
  uniqBy,
  uniqueId,
  values,
  xor,
} from 'lodash/fp'
export {default as applySpec} from 'ramda/src/applySpec'
export {default as ifElse} from 'ramda/src/ifElse'
export {default as tap} from 'ramda/src/tap'
export {useScrollbarWidth} from 'react-use'
export {compose, pipe} from 'ts-pipe-compose'
export {constant, defaultTo, divide, flow, isEmpty, isNull, isUndefined, multiply, negate, noop, stubString}

// экспериментальный ifThen, на случай когда ветка else не нужна
// экспериментальный, потому что не очень хочу возиться с типизацией
export function ifThen<T>(predicate: Function, mapper: Function) {
  return function _ifThen(value?: T) {
    if (predicate(value)) {
      return mapper(value)
    }
  }
}

export function logGroupStart(label: string, collapsed: boolean = false) {
  if (process.env.NODE_ENV === 'development') {
    return function _logGroupStart() {
      collapsed ? console.groupCollapsed(label) : console.group(label)
    }
  }

  return noop
}

export function logGroupEnd() {
  if (process.env.NODE_ENV === 'development') {
    return console.groupEnd
  }

  return noop
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function log(...messages: any[]) {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function _log(...args: any[]): void {
      return console.log.apply(console, messages.concat(args))
    }
  }

  return noop
}

export function tag2url(tag: string): string {
  return `https://ya-authproxy.taxi.yandex.ru/3.0/getimage?tag=${tag}&size_hint=9999`
}

export const toNumber = (value: unknown, defaultValue = 0): number => {
  // важная штука (NaN || 0) вернет 0
  // лодаш не подходит потому что возвращает NaN, смысла его обрабатывать отдельно я не вижу
  return Number(value) || defaultValue
}

export const isFilled = negate(isEmpty) as <T>(v?: T) => v is T

export const isNotUndefined = negate(isUndefined) as <T>(v?: T) => v is T

export const isNotNull = negate(isNull) as <T>(v: T | null) => v is T

export const delay = (timeoutMs: number = 1000) => new Promise<void>(resolve => setTimeout(resolve, timeoutMs))

export function identity<T extends unknown>(v: T) {
  return v
}

export function modelPath<Model>() {
  type ValidatePathFunction = <P extends PathsIn<Model>>(path: P) => P
  // юзаем identity, жрем на пару байт оперативы меньше 🥷
  return identity as ValidatePathFunction
}

export function absurd(arg: never): never {
  throw new Error(`Non-exhaustive switch statement with ${arg}`)
}

export function raise(msg: string = 'unknown error'): never {
  throw new Error(msg)
}

const divideTo100 = (number: number) => divide(number, 100)

export const multiplyTo100 = (number: number) => multiply(number, 100)

export const multiplyTo1000 = (number: number) => multiply(number, 1000)

const roundTo3 = (number: number) => toNumber(number.toFixed(3))

export const toDefaultZero = defaultTo(0)

export const toDefaultEmptyString: (value?: string) => string = defaultTo(stubString())

export const toGrams: (value: number) => number = flow(multiplyTo1000, roundTo3)

export const toCentimeter: (meter: number) => number = flow(multiplyTo100, roundTo3)

export const toMeter: (value?: string) => number = flow(toNumber, divideTo100, roundTo3)

export const toKopecks: (value: number) => number = flow(multiplyTo100, roundTo3)

export const invert: (value: unknown) => boolean = negate(Boolean)

export const toUndefined: () => undefined = constant(undefined)

export const toUndefinedIfEmpty: <T>(value: T) => T | undefined = ifElse(isEmpty, toUndefined, identity)

export const isValidEmail = (email: string) => {
  const EMAIL_REG_EXP: RegExp =
    /^(([^а-яА-ЯёЕ<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Zа-яА-ЯёЕ\-0-9]+\.)+[a-zA-Zа-яА-ЯёЕ]{2,}))$/

  return EMAIL_REG_EXP.test(email)
}

export const isValidOrderNumber = (orderNumber?: string): boolean => {
  if (isUndefined(orderNumber)) {
    return false
  }

  const ORDER_NUMBER_REG_EXP: RegExp = /^(\w|-|\/|\\)+$/

  return ORDER_NUMBER_REG_EXP.test(orderNumber)
}

// max из lodash никак не хотел работать с getID, тип победить не смог и использовал нативный Math.max
export const max = (nums: number[]) => Math.max(...nums)

export function parseJSON(value?: string) {
  try {
    return value ? JSON.parse(value) : undefined
  } catch (error) {
    return undefined
  }
}

